import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { Color, IClaims, Orientation } from '@lsq-frontend/common-ui';
import { Icons, FeatureFlagService, ClaimService } from '@lsq-frontend/common-ui';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import { I18n } from '@ngx-translate/i18n-polyfill';
import {
    AdminClaimState,
    IClaimRowUiControl,
    IPermissionsUiControl,
    ManageContactState
} from '../../settings.models';
import { ManageUsersService } from '../../../services/manage-users.service';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';

@Component({
    selector: 'admin-claims',
    templateUrl: './admin-claims.component.html',
    styleUrls: ['./admin-claims.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AdminClaimComponent {
    @Input() public readOnly = true;
    @Input('newUser') public isNewUser = false;
    @Input() public allOff = false;
    @Output() public claimsChange = new EventEmitter<IClaims>();

    public _adminClaimState = new AdminClaimState();

    //Admin claims
    public enableAdminPermissionsHeader = this._i18n({
        value: 'Enable Admin Permissions',
        meaning: 'Enable Admin Permissions header'
    });

    public enableAdminManagementPermissionsHeader = this._i18n({
        value: 'Admin User Management',
        meaning: 'Enable Admin Management Permissions header'
    });

    public enableAdminManagementPermissionsDescription = this._i18n({
        value: 'Access to invite new Admins and edit user permissions.',
        meaning: 'Enable Admin Permissions description'
    });

    public buyerPermissionsHeader = this._i18n({
        value: 'Buyer Permissions',
        meaning: 'Buyer Permissions header'
    });
    public buyerPermissionsDescription = this._i18n({
        value: 'Access to the Buyer Creation and Configuration.',
        meaning: 'Buyer Permissions description'
    });

    public supplierPermissionsHeader = this._i18n({
        value: 'Supplier Permissions',
        meaning: 'Supplier Permissions header'
    });
    public supplierPermissionsDescription = this._i18n({
        value:
            'Access to Supplier Creation, Configuration, Enrollment Invites, Payment Methods, Contacts, and Fees.',
        meaning: 'Supplier permission description'
    });

    public prospectPermissionsHeader = this._i18n({
        value: 'Prospect Permissions',
        meaning: 'Prospect permission header'
    });
    public prospectPermissionsDescription = this._i18n({
        value: 'Access to the prospect portal to invite and onboard factoring clients.',
        meaning: 'Prospect permission description'
    });

    public invoicePermissionsHeader = this._i18n({
        value: 'Admin Invoice Permissions',
        meaning: 'Admin Invoice permission header'
    });
    public invoicePermissionsDescription = this._i18n({
        value: 'Access to the admin invoice portal.',
        meaning: 'Admin Invoice permission description'
    });

    public manageDocumentsHeader = this._i18n({
        value: 'Manage Documents',
        meaning: 'Manage Documents permission header'
    });
    public manageDocumentsDescription = this._i18n({
        value: 'View and submit requested documents.',
        meaning: 'Manage Documents permission description'
    });

    // Constants.
    public orientation = Orientation;
    public icons = Icons;
    public color = Color;

    constructor(
        private _manageUsersService: ManageUsersService,
        private _featureFlagService: FeatureFlagService,
        protected _i18n: I18n,
        private _claimService: ClaimService
    ) {}

    public adminClaimsIsOn$ = this._featureFlagService.isOn(FeatureFlags.AdminClaims);
    private _documentCenterIsOn = this._featureFlagService.isOnSync(FeatureFlags.DocumentCenter);

    public ngOnInit(): void {
        this._adminClaimState = new AdminClaimState();

        const claims = this._manageUserState.claims || [];
        this._adminClaimState.adminClaimManagementUiControls = this.createAdminClaimManagementUiControls().map(
            (adminControl) => ({
                ...adminControl,
                viewIsOn: adminControl.viewClaims.every((claim) => claims.indexOf(claim) >= 0),
                editIsOn: adminControl.editClaims.every((claim) => claims.indexOf(claim) >= 0)
            })
        );

        const switchAdminClainManagement = this.createSystemAdminClaimManagementUiControl();
        this._adminClaimState.adminSystemClaimManagementUiControl = {
            ...switchAdminClainManagement,
            isOn: switchAdminClainManagement.claims.every((claim) => claims.indexOf(claim) >= 0)
        };

        const switchAdminManagement = this.createSystemAdminManagementUiControl();
        this._adminClaimState.adminSystemManagementUiControl = {
            ...switchAdminManagement,
            isOn: switchAdminManagement.claims.every((claim) => claims.indexOf(claim) >= 0)
        };

        if (this.allOff) {
            this._adminClaimState.adminClaimManagementUiControls.forEach((permisson) => {
                permisson.viewIsOn = false;
                permisson.editIsOn = false;
            });
            this._adminClaimState.adminSystemClaimManagementUiControl.isOn = false;
            this._adminClaimState.adminSystemManagementUiControl.isOn = false;
        }
    }

    public get adminSystemManagementUiControl(): IPermissionsUiControl {
        return this._adminClaimState.adminSystemManagementUiControl || null;
    }

    public get adminClaimManagementUiControls(): IClaimRowUiControl[] {
        return this._adminClaimState.adminClaimManagementUiControls || [];
    }

    public get adminSystemClaimManagementUiControl(): IPermissionsUiControl {
        return this._adminClaimState.adminSystemClaimManagementUiControl || null;
    }

    public emitChanges(claims) {
        this._manageUsersService.setAdminClaims = this._adminClaimState;
        this.claimsChange.emit(claims);
    }

    public emitChangesRowClaim(claims, permissionControl) {
        const currentClaim = this._adminClaimState.adminClaimManagementUiControls[
            this._adminClaimState.adminClaimManagementUiControls.indexOf(permissionControl)
        ];
        currentClaim.editIsOn = claims[0];
        currentClaim.viewIsOn = claims[1];
        this.emitChanges(claims);
    }

    public isSwitchDisabled(permissionControl: IPermissionsUiControl): boolean {
        return this.readOnly || !!permissionControl.isReadOnly || permissionControl.isDisabled;
    }

    public createSystemAdminClaimManagementUiControl(): IPermissionsUiControl {
        let permissions: IPermissionsUiControl = null;

        if (this._claimService.isOnSync(DashboardClaim.SystemAdminClaimManagement)) {
            permissions = {
                label: this.enableAdminManagementPermissionsHeader,
                description: this.enableAdminManagementPermissionsDescription,
                claims: [DashboardClaim.SystemAdminClaimManagement]
            };
        }

        return permissions;
    }

    public createSystemAdminManagementUiControl(): IPermissionsUiControl {
        let permissions: IPermissionsUiControl = null;

        if (this._claimService.isOnSync(DashboardClaim.SystemManagement)) {
            permissions = {
                label: this.enableAdminPermissionsHeader,
                description: '',
                claims: [DashboardClaim.SystemManagement, DashboardClaim.Admin]
            };
        }

        return permissions;
    }

    public createAdminClaimManagementUiControls(): IClaimRowUiControl[] {
        let permissions: IClaimRowUiControl[] = [];

        if (this._claimService.isOnSync(DashboardClaim.SystemAdminClaimManagement)) {
            permissions = permissions.concat([
                {
                    label: this.buyerPermissionsHeader,
                    description: this.buyerPermissionsDescription,
                    editClaims: [DashboardClaim.AdminEditBuyer],
                    viewClaims: [DashboardClaim.AdminViewBuyer]
                },
                {
                    label: this.supplierPermissionsHeader,
                    description: this.supplierPermissionsDescription,
                    editClaims: [DashboardClaim.AdminEditSupplier],
                    viewClaims: [DashboardClaim.AdminViewSupplier]
                },
                {
                    label: this.prospectPermissionsHeader,
                    description: this.prospectPermissionsDescription,
                    editClaims: [DashboardClaim.AdminEditProspect],
                    viewClaims: [DashboardClaim.AdminViewProspect]
                },
                {
                    label: this.invoicePermissionsHeader,
                    description: this.invoicePermissionsDescription,
                    editClaims: [DashboardClaim.AdminEditInvoice],
                    viewClaims: [DashboardClaim.AdminViewInvoice]
                }
            ]);

            if (this._documentCenterIsOn) {
                permissions = permissions.concat([
                    {
                        label: this.manageDocumentsHeader,
                        description: this.manageDocumentsDescription,
                        editClaims: [DashboardClaim.AdminEditDocument],
                        viewClaims: [DashboardClaim.AdminViewDocument]
                    }
                ]);
            }
        }

        return permissions;
    }

    public get _manageUserState(): ManageContactState {
        return this.isNewUser
            ? this._manageUsersService.addUserState
            : this._manageUsersService.activeManageUserState;
    }
}
